import { gql, makeVar, useApolloClient, useReactiveVar } from "@apollo/client";
import { detect } from "detect-browser";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { moduleType, VideoResolution } from "../__generated__/globalTypes";
import { setIsPlaying } from "../components/Video/useVideo";
import { useViewport, View } from "../components/Viewport/useViewport";
import { createTelemetryEvent } from "../telemetry/Telemetry";
import { testCourse, testModule } from "../util/fakeCourse";
import { determineOptimalQuality } from "../util/videoQuality";
import { ChangeSlideFragment } from "./__generated__/ChangeSlideFragment";
import { CourseFragment } from "./__generated__/CourseFragment";
import { FullCourseFragment } from "./__generated__/FullCourseFragment";
import {
  ModuleFragment,
  ModuleFragment_videoSuffixes_mp4,
  ModuleFragment_videoSuffixes_webm,
} from "./__generated__/ModuleFragment";
import { ModuleQuery } from "./__generated__/ModuleQuery";
import { SectionFragment } from "./__generated__/SectionFragment";
import { SlideFragment } from "./__generated__/SlideFragment";
import { TrainingInstanceFragment } from "./__generated__/TrainingInstanceFragment";
import { useErrorHandler } from "./useErrorHandler";
import { usePersistedSettings } from "./usePersistedSettings";
import { useTestMode } from "./useTestMode";

export interface AppState {
  courseId?: number | null;
  instanceId?: string | null;
  sectionId?: string | null;
  moduleId?: string | null;
  slideId?: string | null;
  slideTime?: string | null;
  videoQuality?: VideoResolution | "auto";
  course?: CourseFragment | FullCourseFragment;
  instance?: TrainingInstanceFragment;
  module?: ModuleFragment;
  slide?: SlideFragment;
  videoPath?: string | null;
}

type AvailableQuality = keyof (ModuleFragment_videoSuffixes_mp4 | ModuleFragment_videoSuffixes_webm);
type ModuleWithSlides = { id: string; slides: Array<{ id: string }> };

export const stateVar = makeVar<AppState>({});
export const loadingVar = makeVar<{ module: string | undefined }>({ module: undefined });

export const MODULE_QUERY = gql`
  query ModuleQuery($moduleId: String!, $instanceId: String, $courseId: Int) {
    module(moduleId: $moduleId, courseId: $courseId) {
      sectionId
      id
      name
      type
      baseUrl
      fullNumber
      quizInProgress
      quizScore
      videoSuffixes {
        mp4 {
          SD
          HD
          FHD
        }
        webm {
          SD
          HD
          FHD
        }
      }
      slides {
        id
        name
        number
        fullNumber
        duration
        thumbnailPath
        captionsUrl
        cloudflareVideo {
          signedUrl
          expiresAt
        }
        moduleId
        lastView(instanceId: $instanceId) {
          slideId
          resumeTime
        }
      }
      lastView @client {
        slideId
        resumeTime
      }
      cookies {
        key
        value
        path
        domain
        expires
        secure
      }
      captionPaths {
        language
        path
      }
    }
  }
`;

export const SLIDE_LOOKUP_QUERY = gql`
  query SlideLookupQuery($courseId: Int!) {
    course(courseId: $courseId) {
      trainingId
      sections {
        id
        modules {
          id
          slides {
            id
            moduleId
          }
        }
      }
    }
  }
`;

export const CHANGE_SLIDE_FRAGMENT = gql`
  fragment ChangeSlideFragment on Slide {
    moduleId
  }
`;

export const SLIDE_FRAGMENT = gql`
  fragment SlideFragment on Slide {
    id
    name
    number
    fullNumber
    duration
    thumbnailPath
    captionsUrl
    cloudflareVideo {
      signedUrl
      expiresAt
    }
    moduleId
    lastView {
      resumeTime
    }
  }
`;

export const MODULE_FRAGMENT = gql`
  fragment ModuleFragment on Module {
    sectionId
    id
    name
    type
    baseUrl
    fullNumber
    quizInProgress
    quizScore
    videoSuffixes {
      mp4 {
        SD
        HD
        FHD
      }
      webm {
        SD
        HD
        FHD
      }
    }
    slides {
      id
      name
      number
      fullNumber
      duration
      thumbnailPath
      captionsUrl
      cloudflareVideo {
        signedUrl
        expiresAt
      }
      moduleId
      lastView {
        resumeTime
      }
    }
    lastView @client {
      slideId
      resumeTime
    }
    cookies {
      key
      value
      path
      domain
      expires
      secure
    }
    captionPaths {
      language
      path
    }
  }
`;

export const SECTION_FRAGMENT = gql`
  fragment SectionFragment on Section {
    id
    name
    surveyUrl
  }
`;

export const TRAINING_INSTANCE_FRAGMENT = gql`
  fragment TrainingInstanceFragment on TrainingInstance {
    id
    expirationDateTime
    userId
    progress {
      instanceId
      viewedSeconds
      totalSeconds
      viewedSlides
      totalSlides
      totalQuizzes
      passedQuizzes
      complete
      lastView {
        slideId
        resumeTime
        dateTimeViewed
      }
      moduleProgress {
        id
        name
        type
        fullNumber
        viewedSlides
        totalSlides
        quizScore
      }
    }
  }
`;

export const COURSE_FRAGMENT_QUERY = gql`
  query CourseFragmentQuery($courseId: Int!) {
    course(courseId: $courseId) {
      id
      trainingId
      name
      courseType
      shortName
      messages {
        text
        html
        link
        startDateTime
        endDateTime
      }
      fullVersion
      version
      sections {
        id
        name
        modules {
          id
          name
        }
      }
    }
  }
`;

export const COURSE_FRAGMENT = gql`
  fragment CourseFragment on Course {
    id
    trainingId
    name
    courseType
    shortName
    messages {
      text
      html
      link
      startDateTime
      endDateTime
    }
    fullVersion
    version
    sections {
      id
      name
      surveyUrl
      modules {
        id
        name
        type
        duration
      }
    }
  }
`;

export const FULL_COURSE_FRAGMENT = gql`
  fragment FullCourseFragment on Course {
    id
    trainingId
    name
    courseType
    shortName
    messages {
      text
      html
      link
      startDateTime
      endDateTime
    }
    contributors {
      id
      bio {
        firstName
        lastName
      }
      profile {
        name
        image
        text
        link
      }
    }
    resources {
      title
      resourceHost
      linkJson
    }
    fullVersion
    version
    sections {
      id
      name
      surveyUrl
      modules {
        id
        name
        type
        duration
        quizScore
        confidenceRating {
          moduleId
          rating
        }
      }
    }
  }
`;

export function setAppState(newState: Partial<AppState>): void {
  stateVar({ ...stateVar(), ...newState });
}

function setLoading(newState: Partial<{ module: string | undefined }>): void {
  loadingVar({ ...loadingVar(), ...newState });
}

export function useAppState() {
  const { testMode, testSlides } = useTestMode();
  const client = useApolloClient();
  const appState = useReactiveVar(stateVar);
  const loading = useReactiveVar(loadingVar);
  const { changeView } = useViewport();
  const { quality, setQuality } = usePersistedSettings();
  const { throwError } = useErrorHandler();

  const useMp4 = useMemo(() => {
    const browser = detect();
    return browser?.name === "safari" || browser?.os === "iOS";
  }, []);

  const module = useMemo(() => {
    if (!testMode && !appState.moduleId) return null;

    return testMode ? { ...testModule, slides: Object.values(testSlides) } : (appState.module ?? undefined);
  }, [appState.moduleId, testMode, testSlides, appState.module]);

  const userId = useMemo(() => {
    return appState.instance?.userId;
  }, [appState.instance]);

  const slide = useMemo(() => {
    if (appState.slideId === undefined || !module) return null;

    return testMode ? testSlides[appState.slideId ?? 0] : appState.slide;
  }, [appState.slideId, module, testMode, testSlides, appState.slide]);

  const section = useMemo(() => {
    if (!appState.sectionId) return null;

    return client.readFragment<SectionFragment>({
      id: `Section:${appState.sectionId}`,
      fragment: SECTION_FRAGMENT,
    });
  }, [appState.sectionId, client]);

  const course = useMemo(() => {
    return appState.course;
  }, [appState.course]);

  const slideIdx = useMemo(() => {
    return {
      index: module?.slides.findIndex(slide => slide.id === appState.slideId),
      slidesLength: module?.slides.length,
    };
  }, [appState.slideId, module]);

  const moduleIdx = useMemo(() => {
    const modules = course?.sections.map(section => section.modules).flat();

    return {
      index: modules?.findIndex(module => module.id === appState.moduleId),
      modulesLength: modules?.length,
    };
  }, [course, appState.moduleId]);

  const hasModuleInActiveCourse = useCallback(
    (moduleIdToFind: string): boolean => {
      if (!course?.sections.length) return true;
      return course.sections.some(section => section.modules.some(module => module.id === moduleIdToFind));
    },
    [course],
  );

  const hasModulePayloadInActiveCourse = useCallback(
    (moduleToValidate: ModuleWithSlides): boolean => {
      if (!course?.sections.length) return true;

      const courseModule = course.sections
        .flatMap(section => section.modules)
        .find(moduleInCourse => moduleInCourse.id === moduleToValidate.id);

      if (!courseModule) return false;
      if (!("slides" in courseModule)) return true;

      const courseSlideIds = new Set(courseModule.slides?.map(slideInCourse => slideInCourse.id) ?? []);
      return moduleToValidate.slides.every(slideInModule => courseSlideIds.has(slideInModule.id));
    },
    [course],
  );

  const setCourse = useCallback(
    (courseId?: number): void => {
      const previousCourseId = stateVar().courseId;
      const courseChanged = previousCourseId !== courseId;

      if (courseChanged) {
        setLoading({ module: undefined });
      }

      setAppState({
        courseId,
        ...(courseChanged
          ? {
              instanceId: undefined,
              sectionId: undefined,
              moduleId: undefined,
              slideId: undefined,
              slideTime: undefined,
              instance: undefined,
              module: undefined,
              slide: undefined,
              videoPath: undefined,
            }
          : {}),
        course: testMode
          ? testCourse
          : (client.readFragment<CourseFragment>({
              id: `Course:{"trainingId":${courseId}}`,
              fragment: FULL_COURSE_FRAGMENT,
            }) ??
            client.readFragment<CourseFragment>({
              id: `Course:{"trainingId":${courseId}}`,
              fragment: COURSE_FRAGMENT,
            }) ??
            undefined),
      });
    },
    [client, testMode],
  );

  const setVideoQuality = (userQuality: VideoResolution | "auto" = quality): void => {
    setQuality(userQuality);
    setAppState({ videoQuality: userQuality });
  };

  const getSmallerResolution = (
    availableQualities: ModuleFragment_videoSuffixes_mp4 | ModuleFragment_videoSuffixes_webm,
  ) => {
    // Smallest to largest
    if (availableQualities.SD) return VideoResolution.SD;
    if (availableQualities.HD) return VideoResolution.HD;
    if (availableQualities.FHD) return VideoResolution.FHD;
  };

  const getMediumResolution = (
    availableQualities: ModuleFragment_videoSuffixes_mp4 | ModuleFragment_videoSuffixes_webm,
  ) => {
    // Medium, then largest (scale down), then smallest (scale up)
    if (availableQualities.HD) return VideoResolution.HD;
    if (availableQualities.FHD) return VideoResolution.FHD;
    if (availableQualities.SD) return VideoResolution.SD;
  };

  const getHigherResolution = (
    availableQualities: ModuleFragment_videoSuffixes_mp4 | ModuleFragment_videoSuffixes_webm,
  ) => {
    // Largest to smallest
    if (availableQualities.FHD) return VideoResolution.FHD;
    if (availableQualities.HD) return VideoResolution.HD;
    if (availableQualities.SD) return VideoResolution.SD;
  };

  const determineVideoQualityByWidth = useCallback(
    (
      availableQualities: ModuleFragment_videoSuffixes_mp4 | ModuleFragment_videoSuffixes_webm,
    ): VideoResolution | undefined => {
      if (window.innerWidth <= 480) {
        return getSmallerResolution(availableQualities);
      } else if (window.innerWidth > 480 && window.innerWidth <= 720) {
        return getMediumResolution(availableQualities);
      } else if (window.innerWidth > 720) {
        return getHigherResolution(availableQualities);
      }

      return undefined;
    },
    [],
  );

  const determineVideoQuality = useCallback(
    (
      availableQualities: ModuleFragment_videoSuffixes_mp4 | ModuleFragment_videoSuffixes_webm,
    ): VideoResolution | undefined => {
      // Convert available qualities to VideoResolution array
      const availableQualitiesArray: VideoResolution[] = [];
      if (availableQualities.FHD) availableQualitiesArray.push(VideoResolution.FHD);
      if (availableQualities.HD) availableQualitiesArray.push(VideoResolution.HD);
      if (availableQualities.SD) availableQualitiesArray.push(VideoResolution.SD);

      // Manual mode: respect user selection
      if (quality !== "auto") {
        const userQuality = quality as VideoResolution;
        if (availableQualitiesArray.includes(userQuality)) {
          return userQuality;
        }
      }

      // Auto mode: use bandwidth-based selection
      try {
        // Get network speed from existing NetworkTesting
        const networkSpeedData = localStorage.getItem("NETWORK_SPEED");
        if (networkSpeedData) {
          const { mbps } = JSON.parse(networkSpeedData);
          if (mbps && mbps > 0) {
            return determineOptimalQuality(mbps, availableQualitiesArray);
          }
        }
      } catch (error) {
        console.warn("Failed to get network speed for quality selection:", error);
      }

      // Fallback to width-based selection if no bandwidth data
      return determineVideoQualityByWidth(availableQualities);
    },
    [quality, determineVideoQualityByWidth],
  );

  const getVideoQuality = useCallback(
    (
      availableQualities: ModuleFragment_videoSuffixes_mp4 | ModuleFragment_videoSuffixes_webm,
    ): VideoResolution | undefined => {
      return determineVideoQuality(availableQualities);
    },
    [determineVideoQuality],
  );

  const getQualitySuffix = useCallback(
    (
      availableQualities: ModuleFragment_videoSuffixes_mp4 | ModuleFragment_videoSuffixes_webm,
      targetQuality?: VideoResolution,
    ): string | null => {
      if (!targetQuality || !(targetQuality in availableQualities)) return null;
      return availableQualities[targetQuality as AvailableQuality] || null;
    },
    [],
  );

  const calculateVideoPath = useCallback(
    (slideNumber: number, formatOverride?: "mp4" | "webm"): string | null => {
      if (!module?.videoSuffixes) return null;

      const number = String(slideNumber).padStart(3, "0");
      const pathPrefix = `/video/${number}`;
      const format = formatOverride ?? (useMp4 ? "mp4" : "webm");

      let availableQualities = module.videoSuffixes[format];
      if (!availableQualities) {
        const fallbackFormat = format === "mp4" ? "webm" : "mp4";
        availableQualities = module.videoSuffixes[fallbackFormat];
        if (!availableQualities) return null;
      }

      const currentQuality = appState.videoQuality !== "auto" ? appState.videoQuality : undefined;
      let qualitySuffix = getQualitySuffix(availableQualities, currentQuality);

      if (!qualitySuffix) {
        const autoQuality = getVideoQuality(availableQualities);

        qualitySuffix = getQualitySuffix(availableQualities, autoQuality);
      }

      if (!qualitySuffix) {
        throw new Error("No quality suffix found");
      }

      return `${pathPrefix}${qualitySuffix}`;
    },
    [module?.videoSuffixes, appState.videoQuality, useMp4, getVideoQuality, getQualitySuffix],
  );

  const setVideoPath = useCallback(
    (slideNumber: number, formatOverride?: "mp4" | "webm"): void => {
      const videoPath = calculateVideoPath(slideNumber, formatOverride);
      setAppState({ videoPath });
    },
    [calculateVideoPath],
  );

  const setInstance = useCallback(
    (instanceId: string): void => {
      const instance =
        client.readFragment<TrainingInstanceFragment>({
          id: `TrainingInstance:${instanceId}`,
          fragment: TRAINING_INSTANCE_FRAGMENT,
        }) ?? undefined;
      setAppState({ instanceId, instance });
    },
    [client],
  );

  const refreshModule = useCallback(async () => {
    const currentInstanceId = stateVar().instanceId ?? appState.instanceId ?? undefined;
    const moduleQuery = appState.moduleId
      ? client.readQuery<ModuleQuery>({
          query: MODULE_QUERY,
          variables: {
            moduleId: appState.moduleId,
            instanceId: currentInstanceId,
            courseId: appState.courseId ?? undefined,
          },
        })
      : undefined;
    const module = moduleQuery?.module as ModuleFragment | undefined;
    const slide = module?.slides.find(moduleSlide => moduleSlide.id === appState.slideId) as SlideFragment | undefined;

    setAppState({ module, slide });
    if (appState.courseId) setCourse(appState.courseId);
  }, [client, appState.courseId, appState.instanceId, appState.moduleId, appState.slideId, setCourse]);

  const fireChangeSlideTelemetryEvent = useCallback((slideInfo: string) => {
    // Only fire telemetry event if we have meaningful slide info
    if (slideInfo && slideInfo.trim() !== "") {
      createTelemetryEvent({
        feature: "playback",
        action: "change slide",
        action_meta: slideInfo,
        media_time: 0,
      }).fire("telemetry");
    }
  }, []);

  const setSlideRef = useRef<((slideId?: string, setTime?: string | boolean) => Promise<void>) | null>(null);
  const setModuleRequestRef = useRef(0);
  const setSlideRequestRef = useRef(0);

  const updateSlideState = useCallback(
    (module: ModuleFragment, slides: ModuleFragment["slides"], prev: boolean, newAppState: Partial<AppState>): void => {
      if (module.type === moduleType.QUIZ) {
        changeView(View.Quiz);
        return;
      }

      if (module.type === moduleType.SURVEY) {
        changeView(View.Survey);
        return;
      }

      changeView(testMode ? View.Test : View.Video);

      if (!slides.length) {
        newAppState.slideId = null;
        newAppState.slideTime = null;
        return;
      }

      newAppState.slideId = slides[prev ? slides.length - 1 : 0].id;
      newAppState.slideTime = module.lastView ? module.lastView.resumeTime : "00:00:00";
      setSlideRef.current?.(newAppState.slideId, newAppState.slideTime ?? undefined);
    },
    [changeView, testMode],
  );

  const pausePlaybackForModuleChange = useCallback(
    (moduleId?: string): void => {
      if (moduleId !== appState.moduleId) {
        setIsPlaying(false);
      }
    },
    [appState.moduleId],
  );

  const clearInvalidModuleState = useCallback((moduleId?: string): void => {
    console.warn("setModule: module payload does not match active course", {
      moduleId,
      courseId: stateVar().courseId,
    });
    setAppState({
      moduleId: undefined,
      sectionId: undefined,
      slideId: undefined,
      module: undefined,
      slide: undefined,
    });
    setLoading({ module: undefined });
  }, []);

  const setModule = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    async (moduleId?: string, updateSlide = true, prev = false, wasEval = false): Promise<void> => {
      const requestId = ++setModuleRequestRef.current;
      const courseIdAtRequestStart = stateVar().courseId;
      const isLatestRequest = () => requestId === setModuleRequestRef.current;
      const isActiveCourse = () => testMode || courseIdAtRequestStart === stateVar().courseId;
      pausePlaybackForModuleChange(moduleId);
      setAppState({ moduleId });
      if (updateSlide) setAppState({ slideId: undefined, slide: undefined });
      if (!moduleId) return;

      setLoading({ module: moduleId });
      const currentInstanceId = stateVar().instanceId ?? appState.instanceId ?? undefined;

      const queryResult = testMode
        ? undefined
        : await client.query<ModuleQuery>({
            query: MODULE_QUERY,
            variables: {
              moduleId,
              instanceId: currentInstanceId,
              courseId: courseIdAtRequestStart ?? undefined,
            },
          });

      if (!isLatestRequest() || !isActiveCourse()) return;

      const module = testMode
        ? { ...testModule, slides: Object.values(testSlides) }
        : ((queryResult?.data?.module as ModuleFragment | undefined) ??
          client.cache?.readQuery?.<ModuleQuery>({
            query: MODULE_QUERY,
            variables: {
              moduleId,
              instanceId: currentInstanceId,
              courseId: courseIdAtRequestStart ?? undefined,
            },
          })?.module);

      if (!testMode && !hasModuleInActiveCourse(moduleId)) {
        clearInvalidModuleState(moduleId);
        return;
      }

      if (!module) {
        return throwError("Module not found");
      }

      if (!testMode && !hasModulePayloadInActiveCourse(module)) {
        clearInvalidModuleState(moduleId);
        return;
      }

      const { sectionId, slides } = module;

      const newAppState: Partial<AppState> = { moduleId, sectionId, module };

      if (updateSlide) {
        updateSlideState(module, slides, prev, newAppState);
      }

      // Find slide number from slides array
      let slideInfo = "";
      if (newAppState.slideId && slides) {
        const slideIndex = slides.findIndex(slide => slide.id === newAppState.slideId);
        if (slideIndex !== -1) {
          const currentSlide = slideIndex + 1;
          const totalSlides = slides.length;
          slideInfo = `slide: ${currentSlide} of ${totalSlides}`;
        }
      }

      // Fire telemetry event for slide change
      fireChangeSlideTelemetryEvent(slideInfo);

      if (!isLatestRequest() || !isActiveCourse()) return;
      setAppState(newAppState);
      setLoading({ module: undefined });

      if (wasEval) {
        setIsPlaying(false);
      }
    },
    [
      client,
      throwError,
      fireChangeSlideTelemetryEvent,
      testMode,
      testSlides,
      updateSlideState,
      appState.instanceId,
      clearInvalidModuleState,
      hasModuleInActiveCourse,
      hasModulePayloadInActiveCourse,
      pausePlaybackForModuleChange,
    ],
  );

  const loadSlideLookupIfNeeded = useCallback(
    async (slideId: string, isLatestRequest: () => boolean): Promise<ChangeSlideFragment | null> => {
      let changeSlide = client.readFragment<ChangeSlideFragment>({
        id: `Slide:${slideId}`,
        fragment: CHANGE_SLIDE_FRAGMENT,
      });

      if (changeSlide || !appState.courseId) {
        return changeSlide;
      }

      try {
        await client.query({
          query: SLIDE_LOOKUP_QUERY,
          variables: { courseId: appState.courseId },
          fetchPolicy: "network-only",
        });
      } catch (error) {
        if (!isLatestRequest()) return null;
        console.warn("setSlide: failed to fetch slide lookup data", { slideId, error });
        return null;
      }

      if (!isLatestRequest()) return null;

      changeSlide = client.readFragment<ChangeSlideFragment>({
        id: `Slide:${slideId}`,
        fragment: CHANGE_SLIDE_FRAGMENT,
      });

      return changeSlide;
    },
    [client, appState.courseId],
  );

  const getSlideInfoForTelemetry = useCallback(
    (resolvedModuleId: string, slideId: string) => {
      const moduleFragment = client.readFragment<ModuleFragment>({
        id: `Module:${resolvedModuleId}`,
        fragment: MODULE_FRAGMENT,
      });

      const slideIndex = moduleFragment?.slides?.findIndex(slide => slide.id === slideId) ?? -1;
      if (slideIndex === -1 || !moduleFragment?.slides) {
        return "";
      }

      return `slide: ${slideIndex + 1} of ${moduleFragment.slides.length}`;
    },
    [client],
  );

  const getResolvedSlideFromCache = useCallback(
    (resolvedModuleId: string, slideId: string) =>
      client.cache
        ?.readQuery?.<ModuleQuery>({
          query: MODULE_QUERY,
          variables: {
            moduleId: resolvedModuleId,
            instanceId: stateVar().instanceId ?? appState.instanceId ?? undefined,
            courseId: stateVar().courseId ?? undefined,
          },
        })
        ?.module?.slides?.find(s => s.id === slideId),
    [client, appState.instanceId],
  );

  const setSlide = useCallback(
    async (slideId?: string, setTime: string | boolean = false): Promise<void> => {
      if (!slideId || testMode) return setAppState({ slideId });
      const requestId = ++setSlideRequestRef.current;
      const courseIdAtRequestStart = stateVar().courseId;
      const isLatestRequest = () => requestId === setSlideRequestRef.current;
      const isActiveCourse = () => courseIdAtRequestStart === stateVar().courseId;
      const changeSlide = await loadSlideLookupIfNeeded(slideId, isLatestRequest);

      if (!isLatestRequest() || !isActiveCourse()) return;
      const resolvedModuleId = changeSlide?.moduleId;

      if (!resolvedModuleId) {
        console.warn("setSlide: slide not found in cache", { slideId });
        return;
      }

      const slideInfo = getSlideInfoForTelemetry(resolvedModuleId, slideId);

      setAppState({ slideId });

      // Fire telemetry event for slide change
      fireChangeSlideTelemetryEvent(slideInfo);
      changeView(View.Video);

      if (resolvedModuleId !== appState.moduleId) {
        await setModule(resolvedModuleId, false);
      }

      if (!isLatestRequest() || !isActiveCourse()) return;
      // Get slide from the loaded module (has full data) instead of readFragment(SLIDE_FRAGMENT).
      // Slides in course.sections from InitCoursePage have minimal data (id, moduleId, name, etc.) and
      // lack cloudflareVideo/lastView—readFragment would return null. The module from setModule
      // (or already in cache) has full slides from ModuleQuery/initialModule.
      const slide = getResolvedSlideFromCache(resolvedModuleId, slideId);

      if (!slide) {
        console.warn("setSlide: Slide not found in cache", { slideId });
        return;
      }

      const getResumeTime = setTime ? slide?.lastView?.resumeTime : null;
      const slideTime = typeof setTime == "string" ? setTime : getResumeTime;

      if (!isLatestRequest() || !isActiveCourse()) return;
      setAppState({ slide, slideTime });
    },
    [
      fireChangeSlideTelemetryEvent,
      changeView,
      testMode,
      appState.moduleId,
      getResolvedSlideFromCache,
      getSlideInfoForTelemetry,
      loadSlideLookupIfNeeded,
      setModule,
    ],
  );

  useEffect(() => {
    setSlideRef.current = setSlide;
  }, [setSlide]);

  const isLastSlide = useMemo(() => {
    if (!appState.slideId || !module) return false;
    const slideIdx = module?.slides.findIndex(slide => slide.id === appState.slideId);
    return module.slides[slideIdx + 1] === undefined;
  }, [appState.slideId, module]);

  const nextSlide = useCallback(
    (prev = false): void => {
      if (appState.slideId === undefined || !module || slideIdx.index === undefined || slideIdx.index < 0) return;

      const nextSlide = module.slides[slideIdx.index + (prev ? -1 : 1)];

      setSlide(nextSlide?.id);
    },
    [module, setSlide, appState.slideId, slideIdx.index],
  );

  const nextModule = useCallback(
    async (prev = false, wasEval = false): Promise<void> => {
      if (!appState.moduleId || !course || moduleIdx.index === undefined || moduleIdx.index < 0) return;

      const modules = course.sections.map(section => section.modules).flat();
      const nextModule = modules[moduleIdx.index + (prev ? -1 : 1)];

      if (nextModule) {
        await setModule(nextModule.id, true, prev, wasEval);
      }
    },
    [setModule, appState.moduleId, course, moduleIdx.index],
  );

  return {
    appState,
    nextSlide,
    setSlide,
    setModule,
    setCourse,
    setInstance,
    setVideoQuality,
    determineVideoQuality,
    determineVideoQualityByWidth,
    slide,
    module,
    section,
    course,
    isLastSlide,
    nextModule,
    userId,
    slideIdx,
    moduleIdx,
    loading,
    refreshModule,
    setVideoPath,
    updateSlideState,
  };
}
